import { createSlice, createSelector } from '@reduxjs/toolkit'
import qs from 'qs'

const maxCashLength = 100;

const initialState = {
  products: {
    cachedQueries: {},
    cachedTimes: {},
    loading: false,
    currentQuery: null,
    loadingCash: false
  },
  page: 1,
  sort: null,
  filters: {
    cachedQueries: {},
    loading: false,
    error: null,
    currentQuery: null,
  },
  error: null,
}

const getOldestKey = (cachedQueries, cachedTimes) => {
  let time = Date.now()
  let oldestKey

  for (let key in cachedQueries) {
    if (cachedTimes[key] && cachedTimes[key] < time) {
      time = cachedTimes[key]
      oldestKey = key
    }
  }

  return oldestKey
}

const catalog = createSlice({
  name: 'catalog',
  initialState: initialState,
  reducers: {
    requestProducts(state, { payload }) {
      const { cachedQueries } = state.products
      const { key } = payload || {}

      state.products.loading = true

      if (key) {
        cachedQueries[key] = { ...cachedQueries[key], loading: true, error: null }
      }
    },
    requestProductsCash(state, { payload }) {
      state.products.loadingCash = true
    },
    successProducts(state, { payload }) {
      const { key, data, refresh } = payload
      const { cachedQueries, cachedTimes } = state.products

      state.products.currentQuery = key
      if (data && (!cachedQueries[key]?.result || refresh || cachedQueries[key]?.loading)) {
        const cachedQueriesLength = Object.keys(cachedQueries).length

        if (cachedQueriesLength >= maxCashLength) {
          const oldestKey = getOldestKey(cachedQueries, cachedTimes)

          delete cachedQueries[oldestKey]
          delete cachedTimes[oldestKey]
        }

        cachedQueries[key] = data || cachedQueries[key]
        cachedQueries[key].loading = false
        cachedQueries[key].error = null
      }

      cachedTimes[key] = Date.now()

      state.products.loading = false
      state.products.loadingCash = false
      state.error = null

    },
    failureProducts(state, { payload }) {
      const { key, data } = payload
      const { cachedQueries } = state.products

      state.products.currentQuery = key
      state.products.loading = false
      state.error = data

      if (!cachedQueries[key]) {
        cachedQueries[key] = {}
      }
      cachedQueries[key].loading = false
      state.products.loadingCash = false
      cachedQueries[key].error = data
    },
    requestFilters(state) {
      state.filters.loading = true
    },
    successFilters(state, { payload }) {
      const { key, data, refresh } = payload
      const { cachedQueries } = state.filters
      state.filters.currentQuery = key

      if (data && (!cachedQueries[key] || refresh)) {
        cachedQueries[key] = data || []
      }

      state.filters.loading = false
      state.filters.error = null
    },
    failureFilters(state, { payload }) {
      const { key, data } = payload
      state.filters.currentQuery = key
      state.filters.loading = false
      state.filters.error = data
    },
    saveSort(state, { payload }) {
      state.sort = payload ?? null
    },
  },
})

export const fetchProducts =
  ({ onSuccess, onCachedSuccess, onError, refresh = false, errToString = true, updateTimeIfExists = true, ...params } = {}) =>
  async (dispatch, getState, { api }) => {
    const state = getState().catalog
    const key = qs.stringify(params)

    if (state.products.cachedQueries[key]?.loading) return;

    try {
      if (!refresh && state.products.cachedQueries[key]?.result) {
        dispatch(requestProductsCash())
        if (updateTimeIfExists) dispatch(successProducts({ key }))
        onCachedSuccess?.(state.products.cachedQueries[key])
        onSuccess?.()
        return
      } else {
        dispatch(requestProducts({ key }))

        const response = await api.products(params)
        const result = response?.result ?? 'success'
        if (result === 'success') {
          dispatch(successProducts({ key, data: response, refresh }))
          onSuccess?.(response)
        } else {
          onError?.(response);
          dispatch(failureProducts({ key, data: response }))
        }
      }
    } catch (err) {
      onError?.(err);
      dispatch(failureProducts({ key, data: errToString ? err.toString() : err }))
    }
  }

export const fetchFilters =
  ({ refresh = false, errToString = true, ...params }) =>
  async (dispatch, getState, { api }) => {
    const state = getState().catalog
    const key = qs.stringify(params)
    try {
      if (!refresh && state.filters.cachedQueries[key]) {
        dispatch(successFilters({ key }))
        return
      } else {
        dispatch(requestFilters())
        const { data, result } = await api.filters(params)
        if (result === 'success') {
          dispatch(successFilters({ key, data, refresh }))
        } else {
          dispatch(failureFilters({ key, data }))
        }
      }
    } catch (err) {
      dispatch(failureFilters({ key, data: errToString ? err.toString() : err }))
    }
  }

export const getCatalogData = createSelector(
  state => state.catalog,
  catalog => {
    const { products, filters, page, sort, error } = catalog
    const {
      cachedQueries: productsCachedQueries,
      currentQuery: productsCurrentQuery,
      ...productsState
    } = products
    const {
      cachedQueries: filtersCachedQueries,
      currentQuery: filtersCurrentQuery,
      ...filtersState
    } = filters
    const productsCachedQueryData = productsCachedQueries[productsCurrentQuery]
    const filtersCachedQueryData = filtersCachedQueries[filtersCurrentQuery] || []

    return {
      products: {
        ...productsCachedQueryData,
        ...productsState,
      },
      filters: {
        data: filtersCachedQueryData,
        ...filtersState,
      },
      page,
      sort,
      error,
    }
  },
)

export const {
  requestProducts,
  requestProductsCash,
  successProducts,
  failureProducts,
  requestFilters,
  successFilters,
  failureFilters,
  saveSort,
} = catalog.actions

export default catalog.reducer
